import React, { useState } from 'react'
import { camelToKebab } from '../../../Helper/util'
import { SortDownIcon, SortIcon, SortUpIcon } from '../../Icons'
import { ColumnComparator } from './TableBody'
import { RowDataWithKey, TableColumn, TableProps } from './Types'

enum SortDirection {
  ASCENDING = 1,
  DESCENDING = -1
}

interface Sort {
  sortColumnId?: string,
  sortDirection: SortDirection
}

interface SortIconConfig {
  ascending: React.JSX.Element
  descending: React.JSX.Element
  default: React.JSX.Element
}

interface SortableTableHeadProps<T extends object> extends TableProps<T> {
  columns: TableColumn<T>[]
  rows: RowDataWithKey<T>[],
  onSortClick: (sortedRows: T[]) => void
  sortIconConfig?: SortIconConfig
}

const defaultSortIconConfig: SortIconConfig = {
  ascending: <SortUpIcon/>,
  descending: <SortDownIcon/>,
  default: <SortIcon/>,
}

export function SortableTableHead<T extends object> (props: SortableTableHeadProps<T>): React.JSX.Element {
  const [sortSelection, setSortSelection] = useState<Sort>({ sortDirection: SortDirection.ASCENDING })
  const { columns, rows, onSortClick } = props
  const sortIconConfig: SortIconConfig = props?.sortIconConfig || defaultSortIconConfig

  function getSortIcon (columnId: string): React.ReactElement {
    if (columnId === sortSelection.sortColumnId) {
      return (sortSelection.sortDirection === SortDirection.ASCENDING)
        ? sortIconConfig.ascending // <SortUpIcon/>
        : sortIconConfig.descending // <SortDownIcon/>
    }
    return sortIconConfig.default
  }

  function handleSortClick (columnId: string, columnCompareFunction: ColumnComparator) {
    const nextSortDirection = (
      sortSelection.sortColumnId === columnId &&
      sortSelection.sortDirection === SortDirection.DESCENDING
    )
      ? SortDirection.ASCENDING
      : SortDirection.DESCENDING

    setSortSelection({
      sortColumnId: columnId,
      sortDirection: nextSortDirection,
    })

    // ⚠️ Problem with JavaScript Array Mutation (Pass-By-Reference)
    // .sort() is a destructive method.
    // It does not create a new array, it mutates the original array in place.
    // Since the reference is not changed, React thinks nothing has been changed.
    // So, React completely skips re-rendering the UI components.
    // ✅ FIX: Create a shallow copy using the spread operator before sorting!
    const rowsSorted = [...rows].sort((rowA: any, rowB: any) => {
      return nextSortDirection * columnCompareFunction(rowA[columnId], rowB[columnId])
    })
    onSortClick(rowsSorted)
  }

  function headerCell<T extends object> (columnConfig: TableColumn<T>): React.JSX.Element {
    const { displayName, dataType, isSortable = false } = columnConfig
    const columnId: string = String(columnConfig.id)
    return (
      <th key={columnId}>
        <div
          className={`header-cell clickable uid-column-header-${camelToKebab(columnId)}`}
          onClick={isSortable ? () => handleSortClick(columnId, dataType.comparator) : undefined}
        >
          {displayName}
          {isSortable && getSortIcon(columnId)}
        </div>
      </th>
    )
  }

  return (
      <thead>
        <tr>
          {columns.map((column) => headerCell(column))}
        </tr>
      </thead>
  )
}
